import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import Joi from 'joi'
import { t } from 'i18next'
import NumberFormat from 'react-number-format'
import 'react-phone-number-input/style.css'
import { Dialog } from '@mui/material'
import {
  CloseRounded, ErrorOutlineRounded, LockOutlined, MailOutline, PhoneIphoneOutlined, ReplayRounded, VisibilityOffOutlined, VisibilityOutlined,
} from '@mui/icons-material'
import { useDispatch, useSelector } from '../../store'
import { sendEmailUpdateVerification, updatePassword, updatePhone } from '../../store/settings/actionCreator'
import { getPhoneCode, setPhoneVerify } from '../../store/auth/actionCreator'
import { clearFieldError } from '../../store/common/actionCreator'
import {
  email as emailRule, ErrorsState, notRequired, password as passwordRule, passwordRepeat as passwordRepeatRule, phone as phoneRule,
  preventEmptySpaceOnInput, TypeFormError, validateForm,
} from '../../function/validation'
import { E164Number, InputPhone } from '../../component/InputPhone'
import { useAdminTheme } from '../../hook/useAdminTheme'
import l from '../../page/SubscribersPageV2.module.css'
import s from './ChangeDialogsV2.module.css'

/*
 * Change email / password / phone in the v2 admin design — the classic
 * dialogs' flows unchanged (container/settings/PopupChange*: the same Joi
 * rules, the same store actions; the phone sends a code first, then saves
 * with it). Only the look is new. The classic settings page keeps the
 * classic dialogs.
 */

/** the v2 settings dialog: a head, the fields, Cancel + the main button (red with `danger`); also TwoFactorV2's */
export function Shell({ icon, title, sub, onClose, onSubmit, saveLabel, danger, cancelLabel, onCancel, children }: {
  icon: React.ReactNode
  title: string
  sub: string
  onClose: () => void
  onSubmit: (e: React.FormEvent) => void
  saveLabel?: string
  danger?: boolean
  /** the left button — Cancel by default (it closes); e.g. Back with `onCancel` */
  cancelLabel?: string
  onCancel?: () => void
  children: React.ReactNode
}) {
  const { theme } = useAdminTheme()
  return (
    <Dialog open onClose={onClose} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2, overflow: 'visible' } }}>
      <form className={`${l.theme} ${s.box}`} data-theme={theme} onSubmit={onSubmit} noValidate>
        <div className={s.head}>
          <span className={`${s.icon} ${danger ? s.iconDanger : ''}`}>{icon}</span>
          <div className={s.headText}><b>{title}</b><span>{sub}</span></div>
          <button type="button" className={s.close} onClick={onClose} aria-label={t('common.close')}><CloseRounded sx={{ fontSize: 18 }} /></button>
        </div>
        <div className={s.body}>{children}</div>
        <div className={s.foot}>
          <button type="button" className={s.ghost} onClick={onCancel || onClose}>{cancelLabel || 'Cancel'}</button>
          <button type="submit" className={`${s.primary} ${danger ? s.primaryDanger : ''}`}>{saveLabel || t('common.save')}</button>
        </div>
      </form>
    </Dialog>
  )
}

export function Err({ error }: { error: TypeFormError }) {
  if (error === undefined || error === null || error === '') return null
  const list = typeof error === 'object' ? Object.values(error) : [error]
  return <>{list.map((e, i) => <span key={i} className={s.err}><ErrorOutlineRounded sx={{ fontSize: 15 }} />{String(e)}</span>)}</>
}

function Field({ label, error, children }: { label: string; error?: TypeFormError; children: React.ReactNode }) {
  return (
    <label className={s.field}>
      <span className={s.label}>{label}</span>
      {children}
      <Err error={error} />
    </label>
  )
}

function Password({ value, onInput, placeholder, error, autoFocus, autoComplete }: {
  value: string; onInput: (v: string) => void; placeholder: string; error?: TypeFormError; autoFocus?: boolean; autoComplete?: string
}) {
  const [shown, setShown] = React.useState(false)
  return (
    <span className={`${s.inputWrap} ${error ? s.invalid : ''}`}>
      <input
        className={s.input}
        type={shown ? 'text' : 'password'}
        value={value}
        onChange={(e) => onInput(e.target.value.replace(/ /g, ''))}
        placeholder={placeholder}
        autoFocus={autoFocus} // eslint-disable-line jsx-a11y/no-autofocus
        autoComplete={autoComplete}
      />
      <button type="button" className={s.eye} onClick={() => setShown(!shown)} aria-label={shown ? 'Hide password' : 'Show password'} tabIndex={-1}>
        {shown ? <VisibilityOffOutlined sx={{ fontSize: 18 }} /> : <VisibilityOutlined sx={{ fontSize: 18 }} />}
      </button>
    </span>
  )
}

/* ── email ──────────────────────────────────────────────────────────── */

const emailSchema = Joi.object({ email: emailRule, password: passwordRule })

export function ChangeEmailV2({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [errors, setErrors] = React.useState<ErrorsState>(null)

  React.useEffect(() => { setErrors(null); dispatch(clearFieldError) }, [dispatch, email, password])

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const error = validateForm(emailSchema, { email, password })
    if (error) { setErrors(error); return }
    dispatch(sendEmailUpdateVerification(email, password, navigate))
    onClose()
  }

  return (
    <Shell icon={<MailOutline sx={{ fontSize: 19 }} />} title={t('settings.changeEmail')} sub="A link to confirm it goes to the new address" onClose={onClose} onSubmit={submit}>
      <Field label={t('field.label.newEmail')} error={errors?.email}>
        <input
          className={`${s.input} ${errors?.email ? s.invalid : ''}`}
          type="email"
          value={email}
          placeholder={t('field.hint.newEmail')}
          onChange={(e) => preventEmptySpaceOnInput(e, setEmail)}
          autoFocus // eslint-disable-line jsx-a11y/no-autofocus
          autoComplete="email"
        />
      </Field>
      <Field label={t('field.label.password')} error={errors?.password}>
        <Password value={password} onInput={setPassword} placeholder={t('field.hint.password')} error={errors?.password} autoComplete="current-password" />
      </Field>
    </Shell>
  )
}

/* ── password ───────────────────────────────────────────────────────── */

const passwordSchema = Joi.object({
  oldPassword: passwordRule.label(t('field.label.oldPassword')),
  password: passwordRule,
  passwordRepeat: passwordRepeatRule,
})

export function ChangePasswordV2({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch()
  const [oldPassword, setOldPassword] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [passwordRepeat, setPasswordRepeat] = React.useState('')
  const [errors, setErrors] = React.useState<ErrorsState>(null)

  React.useEffect(() => { setErrors(null); dispatch(clearFieldError) }, [dispatch, oldPassword, password, passwordRepeat])

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const error = validateForm(passwordSchema, { oldPassword, password, passwordRepeat })
    if (error) { setErrors(error); return }
    dispatch(updatePassword(oldPassword, password))
    onClose()
  }

  return (
    <Shell icon={<LockOutlined sx={{ fontSize: 19 }} />} title={t('settings.changePassword')} sub="You stay signed in on this device" onClose={onClose} onSubmit={submit}>
      <Field label={t('field.label.oldPassword')} error={errors?.oldPassword}>
        <Password value={oldPassword} onInput={setOldPassword} placeholder={t('field.hint.oldPassword')} error={errors?.oldPassword} autoFocus autoComplete="current-password" />
      </Field>
      <Field label={t('field.label.password')} error={errors?.password}>
        <Password value={password} onInput={setPassword} placeholder={t('field.hint.password')} error={errors?.password} autoComplete="new-password" />
      </Field>
      <Field label={t('field.label.confirmPassword')} error={errors?.passwordRepeat}>
        <Password value={passwordRepeat} onInput={setPasswordRepeat} placeholder={t('field.hint.confirmPassword')} error={errors?.passwordRepeat} autoComplete="new-password" />
      </Field>
    </Shell>
  )
}

/* ── phone ──────────────────────────────────────────────────────────── */

const phoneSchema = Joi.object({ phone: phoneRule.label(t('field.label.phone')), code: notRequired })

export function ChangePhoneV2({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch()
  const { phoneVerify } = useSelector((state) => state.auth)
  const fieldErrors = useSelector((state) => state.common.fieldErrors)
  const [phone, setPhone] = React.useState<E164Number | undefined>('')
  const [code, setCode] = React.useState('')
  const [errors, setErrors] = React.useState<ErrorsState>(null)

  React.useEffect(() => { setErrors(null); dispatch(clearFieldError) }, [dispatch, phone, code])

  function close() {
    onClose()
    dispatch(setPhoneVerify(false))
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const error = validateForm(phoneSchema, { phone, code })
    if (error) { setErrors(error); return }
    if (phoneVerify && phone) dispatch(updatePhone(String(phone), code, close))
    else dispatch(getPhoneCode(phone as E164Number))
  }

  const phoneError = errors?.phone || fieldErrors?.phone
  const codeError = errors?.phoneCode || fieldErrors?.phoneCode
  return (
    <Shell
      icon={<PhoneIphoneOutlined sx={{ fontSize: 19 }} />}
      title={t('settings.changePhoneNumber')}
      sub={phoneVerify ? 'Enter the code sent to the new number' : 'A code is sent to the new number to confirm it'}
      onClose={close}
      onSubmit={submit}
      saveLabel={phoneVerify ? t('common.save') : 'Send code'}
    >
      <Field label={t('field.label.newPhone')} error={phoneError}>
        <span className={`${s.phone} ${phoneError ? s.invalid : ''}`}>
          <InputPhone value={phone} error={phoneError} onChange={setPhone} />
        </span>
      </Field>
      {phoneVerify && (
        <Field label={t('field.label.verificationCode')} error={codeError}>
          <span className={s.codeRow}>
            <NumberFormat
              className={`${s.input} ${s.code} ${codeError ? s.invalid : ''}`}
              value={code}
              onValueChange={(values) => setCode(values.value)}
              format="### ###"
              mask="_"
              autoFocus
            />
            <button type="button" className={s.ghost} onClick={() => dispatch(getPhoneCode(phone as E164Number))}>
              <ReplayRounded sx={{ fontSize: 16 }} />{t('auth.register.sendAgain')}
            </button>
          </span>
        </Field>
      )}
    </Shell>
  )
}
